import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { BsCheckCircleFill, BsTriangleFill } from 'react-icons/bs';
import { useGetIncomeStats } from '../../../services/admin.stats.service';

const IncomeChart = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('This month');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [animationProgress, setAnimationProgress] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const svgRef = useRef(null);
  const dropdownRef = useRef(null);

  const { data: incomeStats, isLoading } = useGetIncomeStats();

  const periods = ['This month', 'This year'];
  
  const monthNames = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

  const processedData = useMemo(() => {
    if (!incomeStats) return null;

    let filtered = [...incomeStats];
    const now = new Date();
    
    if (selectedPeriod === 'This month') {
      // Show last 6 months for "This Month" view to provide context
      const sixMonthsAgo = new Date(now);
      sixMonthsAgo.setMonth(now.getMonth() - 5);
      
      // Generate last 6 months skeleton
      const months = [];
      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        months.push({
          month: monthNames[d.getMonth()],
          monthNum: d.getMonth() + 1,
          year: d.getFullYear(),
          value: 0,
          expenses: 0,
          label: `${monthNames[d.getMonth()]} ${d.getFullYear()}`
        });
      }

      // Fill with real data
      months.forEach(m => {
        const found = incomeStats.find(item => item.month === m.monthNum && item.year === m.year);
        if (found) {
          m.value = found.income;
          m.expenses = found.expenses;
        }
      });
      return months;
    } else {
      // This year view (12 months)
      const months = [];
      for (let i = 0; i < 12; i++) {
        const d = new Date(now.getFullYear(), i, 1);
        months.push({
          month: monthNames[d.getMonth()],
          monthNum: d.getMonth() + 1,
          year: d.getFullYear(),
          value: 0,
          expenses: 0,
          label: `${monthNames[d.getMonth()]} ${d.getFullYear()}`
        });
      }
      
      months.forEach(m => {
        const found = incomeStats.find(item => item.month === m.monthNum && item.year === m.year);
        if (found) {
          m.value = found.income;
          m.expenses = found.expenses;
        }
      });
      return months;
    }
  }, [incomeStats, selectedPeriod]);

  const statsSummary = useMemo(() => {
    if (!processedData) return { totalIncome: 0, target: 0, maxValue: 1000, incomePoints: [], expensePoints: [] };
    
    const totalIncome = processedData.reduce((acc, curr) => acc + curr.value, 0);
    const maxValue = Math.max(...processedData.map(d => Math.max(d.value, d.expenses)), 1000) * 1.2;
    const target = Math.round(maxValue * 0.8 / 1000) * 1000;
    
    return { 
      totalIncome, 
      target, 
      maxValue,
      incomePoints: processedData.map(d => ({ ...d })),
      expensePoints: processedData.map(d => ({ ...d, value: d.expenses }))
    };
  }, [processedData]);

  const currentData = statsSummary;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Animation effect
  useEffect(() => {
    setAnimationProgress(0);
    setHoveredPoint(null);
    const timer = setTimeout(() => {
      const animate = () => {
        setAnimationProgress(prev => {
          if (prev >= 1) return 1;
          return prev + 0.012;
        });
      };
      const animationFrame = setInterval(animate, 16);
      setTimeout(() => clearInterval(animationFrame), 1200);
    }, 200);
    
    return () => clearTimeout(timer);
  }, [selectedPeriod]);

  // Create smooth curve path
  const createPath = (points, progress = 1) => {
    if (points.length < 2) return '';
    
    const visiblePoints = Math.ceil(points.length * progress);
    const animatedPoints = points.slice(0, visiblePoints);

    if (animatedPoints.length < 2) return '';

    let path = `M ${animatedPoints[0].x} ${animatedPoints[0].y}`;
    
    for (let i = 1; i < animatedPoints.length; i++) {
      const prev = animatedPoints[i - 1];
      const curr = animatedPoints[i];
      
      const cpx1 = prev.x + (curr.x - prev.x) * 0.4;
      const cpy1 = prev.y;
      const cpx2 = prev.x + (curr.x - prev.x) * 0.6;
      const cpy2 = curr.y;
      
      path += ` C ${cpx1} ${cpy1}, ${cpx2} ${cpy2}, ${curr.x} ${curr.y}`;
    }
    
    return path;
  };

  const chartWidth = 460;
  const chartHeight = 200;
  const padding = 60;
  
  const points = currentData.incomePoints.map((item, index) => ({
    x: padding + (index * (chartWidth - 2 * padding)) / (currentData.incomePoints.length - 1),
    y: chartHeight - padding - ((item.value / currentData.maxValue) * (chartHeight - 2 * padding)),
    ...item
  }));

  const expensePoints = currentData.expensePoints.map((item, index) => ({
    x: padding + (index * (chartWidth - 2 * padding)) / (currentData.expensePoints.length - 1),
    y: chartHeight - padding - ((item.value / currentData.maxValue) * (chartHeight - 2 * padding)),
    ...item
  }));

  const handleMouseMove = (event) => {
    if (svgRef.current) {
      const rect = svgRef.current.getBoundingClientRect();
      setMousePosition({
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      });
    }
  };

  const handlePointHover = (index, event, type) => {
    setHoveredPoint({ index, type });
    handleMouseMove(event);
  };

  const formatCurrency = (value) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value.toLocaleString()}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'On track': return 'text-emerald-500';
      case 'Ahead': return 'text-blue-500';
      case 'Completed': return 'text-slate-500';
      default: return 'text-slate-500';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'On track': return '🟢';
      case 'Ahead': return '🚀';
      case 'Completed': return '✅';
      default: return '⚪';
    }
  };

  if (isLoading) {
    return (
      <div className="w-full max-w-2xl mx-auto bg-white dark:bg-[#1d2026] rounded-3xl p-8 h-[400px] flex items-center justify-center border border-gray-100 dark:border-[#32353c] shadow-sm">
        <div className="w-8 h-8 border-4 border-[#4318FF] dark:border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto bg-gradient-to-br from-slate-50 to-white dark:from-gray-800 dark:to-gray-900 rounded-3xl p-8 border border-gray-100 dark:border-[#32353c] shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        {/* Period Selector */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center gap-2 bg-[#F4F7FE] dark:bg-gray-700 hover:bg-slate-200 dark:hover:bg-gray-600 px-4 py-2 rounded-xl text-sm font-medium text-[#A3AED0] dark:text-gray-300 transition-all duration-200"
          >
            <Calendar color='#A3AED0' size={20}/>
             {selectedPeriod}
            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          
          {isDropdownOpen && (
            <div className="absolute top-full left-0 mt-2 w-48 bg-white dark:bg-[#1d2026] border border-slate-200 dark:border-[#32353c] rounded-2xl shadow-xl py-2 z-10 animate-in fade-in slide-in-from-top-2 duration-200">
              {periods.map(period => (
                <button
                  key={period}
                  onClick={() => {
                    setSelectedPeriod(period);
                    setIsDropdownOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors duration-150 ${
                    selectedPeriod === period 
                      ? 'bg-blue-50 dark:bg-gray-700 text-blue-700 dark:text-blue-400 border-r-2 border-blue-500' 
                      : 'text-slate-700 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-600'
                  }`}
                >
                  {period}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Target Badge */}
        <div className="bg-[#4318FF] text-white px-4 py-2 rounded-xl text-sm font-bold shadow-lg shadow-indigo-100 dark:shadow-indigo-900/10">
          Target: {formatCurrency(currentData.target)}
        </div>
      </div>

      <div className="w-full flex flex-col md:flex-row justify-between items-center gap-1">
        {/* Main Income Display */}
        <div className="mb-8 w-full">
          <div className="flex items-baseline gap-3 mb-2">
            <h1 className="text-4xl font-bold text-[#2B3674] dark:text-white">
              {formatCurrency(currentData.totalIncome)}
            </h1>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <p className='text-[#A3AED0] dark:text-gray-400 text-[13px] text-nowrap'>Total Period Income</p>
            <BsTriangleFill color='#05CD99' size={8}/>
            <p className='text-[#05CD99] text-[12px] font-bold'>+{(Math.random() * 5 + 2).toFixed(2)}%</p>
          </div>
        
          <div className="flex items-center gap-2 shrink-0 mt-4">
            <div className="w-8 h-8 rounded-full bg-[#05CD99]/10 flex items-center justify-center">
              <BsCheckCircleFill color='#05CD99' size={14}/>
            </div>
            <p className='text-[#05CD99] text-[13px] font-medium tracking-tight'>Sales Analysis: On track</p>
          </div>
        </div>

      {/* Chart */}
      <div className="w-full">
        <div className="w-[fit-content] relative ml-auto">
          <svg 
            ref={svgRef}
            width={chartWidth} 
            height={chartHeight} 
            className="overflow-visible ml-auto"
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setHoveredPoint(null)}
          >
            {/* Gradient definitions */}
            <defs>
              <linearGradient id="incomeGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#10b981" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#10b981" stopOpacity="0.05" />
              </linearGradient>
              <linearGradient id="expenseGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#ef4444" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#ef4444" stopOpacity="0.05" />
              </linearGradient>
            </defs>

            {/* Grid lines */}
            {[...Array(currentData.incomePoints.length)].map((_, index) => (
              <line
                key={index}
                x1={padding + (index * (chartWidth - 2 * padding)) / (currentData.incomePoints.length - 1)}
                y1={padding - 10}
                x2={padding + (index * (chartWidth - 2 * padding)) / (currentData.incomePoints.length - 1)}
                y2={chartHeight - padding + 10}
                stroke="#f1f5f9"
                strokeWidth="1"
                opacity={0.8}
              />
            ))}
            
            {/* Area under income curve */}
            {animationProgress > 0.4 && points.length > 0 && (
              <path
                d={`${createPath(points, animationProgress)} L ${points[Math.min(Math.floor(points.length * animationProgress), points.length - 1)].x} ${chartHeight - padding} L ${points[0].x} ${chartHeight - padding} Z`}
                fill="url(#incomeGradient)"
                opacity={Math.min(animationProgress * 1.2, 0.6)}
              />
            )}
            
            {/* Area under expense curve */}
            {animationProgress > 0.4 && expensePoints.length > 0 && (
              <path
                d={`${createPath(expensePoints, animationProgress)} L ${expensePoints[Math.min(Math.floor(expensePoints.length * animationProgress), expensePoints.length - 1)].x} ${chartHeight - padding} L ${expensePoints[0].x} ${chartHeight - padding} Z`}
                fill="url(#expenseGradient)"
                opacity={Math.min(animationProgress * 1.2, 0.4)}
              />
            )}
            
            {/* Expense line */}
            <path
              d={createPath(expensePoints, animationProgress)}
              fill="none"
              stroke="#ef4444"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                filter: 'drop-shadow(0 4px 8px rgba(239, 68, 68, 0.2))',
              }}
            />
            
            {/* Income line */}
            <path
              d={createPath(points, animationProgress)}
              fill="none"
              stroke="#10b981"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                filter: 'drop-shadow(0 4px 8px rgba(16, 185, 129, 0.2))',
              }}
            />
            
            {/* Invisible hover areas for income */}
            {points.map((point, index) => (
              <circle
                key={`income-${index}`}
                cx={point.x}
                cy={point.y}
                r="20"
                fill="transparent"
                className="cursor-pointer"
                onMouseEnter={(e) => handlePointHover(index, e, 'income')}
                style={{
                  opacity: animationProgress > 0.8 ? 1 : 0,
                  pointerEvents: animationProgress > 0.8 ? 'all' : 'none'
                }}
              />
            ))}
            
            {/* Invisible hover areas for expenses */}
            {expensePoints.map((point, index) => (
              <circle
                key={`expense-${index}`}
                cx={point.x}
                cy={point.y}
                r="20"
                fill="transparent"
                className="cursor-pointer"
                onMouseEnter={(e) => handlePointHover(index, e, 'expense')}
                style={{
                  opacity: animationProgress > 0.8 ? 1 : 0,
                  pointerEvents: animationProgress > 0.8 ? 'all' : 'none'
                }}
              />
            ))}
            
            {/* Visible points on hover */}
            {hoveredPoint !== null && (
              <g>
                {/* Income point */}
                <circle
                  cx={points[hoveredPoint.index].x}
                  cy={points[hoveredPoint.index].y}
                  r="8"
                  fill="white"
                  stroke="#10b981"
                  strokeWidth="3"
                  className="animate-in zoom-in duration-200"
                  style={{
                    filter: 'drop-shadow(0 4px 8px rgba(16, 185, 129, 0.3))',
                    opacity: hoveredPoint.type === 'income' ? 1 : 0.6
                  }}
                />
                <circle
                  cx={points[hoveredPoint.index].x}
                  cy={points[hoveredPoint.index].y}
                  r="4"
                  fill="#10b981"
                  className="animate-in zoom-in duration-200"
                  style={{
                    opacity: hoveredPoint.type === 'income' ? 1 : 0.6
                  }}
                />
                
                {/* Expense point */}
                <circle
                  cx={expensePoints[hoveredPoint.index].x}
                  cy={expensePoints[hoveredPoint.index].y}
                  r="8"
                  fill="white"
                  stroke="#ef4444"
                  strokeWidth="3"
                  className="animate-in zoom-in duration-200"
                  style={{
                    filter: 'drop-shadow(0 4px 8px rgba(239, 68, 68, 0.3))',
                    opacity: hoveredPoint.type === 'expense' ? 1 : 0.6
                  }}
                />
                <circle
                  cx={expensePoints[hoveredPoint.index].x}
                  cy={expensePoints[hoveredPoint.index].y}
                  r="4"
                  fill="#ef4444"
                  className="animate-in zoom-in duration-200"
                  style={{
                    opacity: hoveredPoint.type === 'expense' ? 1 : 0.6
                  }}
                />
              </g>
            )}
            
            {/* X-axis labels */}
            {points.map((point, index) => (
              <text
                key={index}
                x={point.x}
                y={chartHeight - 25}
                textAnchor="middle"
                fontSize="12"
                fill="#64748b"
                fontWeight="500"
                style={{
                  opacity: animationProgress > 0.7 ? 1 : 0,
                  transition: 'opacity 0.4s ease'
                }}
              >
                {point.month}
              </text>
            ))}
            
            {/* Y-axis labels */}
            {points.length > 0 && [0.25, 0.5, 0.75, 1].map((ratio, index) => (
              <text
                key={index}
                x={35}
                y={chartHeight - padding - (ratio * (chartHeight - 2 * padding)) + 5}
                textAnchor="middle"
                fontSize="11"
                fill="#94a3b8"
                fontWeight="500"
                style={{
                  opacity: animationProgress > 0.5 ? 0.8 : 0,
                  transition: 'opacity 0.4s ease'
                }}
              >
                {formatCurrency(currentData.maxValue * ratio)}
              </text>
            ))}
          </svg>

          {/* Empty State Overlay */}
          {points.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <p className="text-slate-400 dark:text-gray-400 text-sm font-medium bg-white/80 dark:bg-[#1d2026]/80 px-4 py-2 rounded-full backdrop-blur-sm">No data for this period</p>
            </div>
          )}
          
          {/* Tooltip */}
          {hoveredPoint !== null && (
            <div 
              className="absolute pointer-events-none z-20 animate-in fade-in slide-in-from-bottom-3 duration-300"
              style={{
                left: Math.min(Math.max(mousePosition.x - 120, 10), chartWidth - 240),
                top: Math.max(mousePosition.y - 90, 10),
              }}
            >
              <div className="bg-[#4318FF] text-white px-4 py-3 rounded-[20px]">
                <div className="text-sm font-bold text-slate-100 mb-1 hidden">
                  {hoveredPoint.type === 'income' 
                    ? currentData.incomePoints[hoveredPoint.index].label 
                    : currentData.expensePoints[hoveredPoint.index].label}
                </div>
                <div className="text-[15px] text-slate-100 font-medium">
                  ${hoveredPoint.type === 'income' 
                    ? currentData.incomePoints[hoveredPoint.index].value 
                    : currentData.expensePoints[hoveredPoint.index].value}
                </div>
                <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-[#4318FF] rotate-45"></div>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default IncomeChart;